import { zodResolver } from '@hookform/resolvers/zod'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { showAlertError } from 'src/components/alertError'
import { useAuth } from 'src/hooks/auth'
import {
  type PixValueStepSchema,
  pixValueStepSchema,
} from 'src/interface/newPixSchema'
import { getBalance } from 'src/requests/transfeera/get-balance'
import { currencyFormat } from 'src/utils/currencyFormat'
import { currencyMask } from 'src/utils/maskInput'
import type { BeneficiaryDataProps, NewPixFormProps } from '.'
import USDCoinSvg from '../../../../../assets/USDBlueCoin.svg'

interface BeneficiaryDataStepProps extends NewPixFormProps {
  beneficiaryData: BeneficiaryDataProps
  setAmount: React.Dispatch<React.SetStateAction<number>>
}

export function BeneficiaryDataStep({
  nextStep,
  beneficiaryData,
  setAmount,
}: BeneficiaryDataStepProps) {
  const { account } = useAuth()
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<PixValueStepSchema>({
    resolver: zodResolver(pixValueStepSchema),
  })

  const maskCpfCnpj = (value: string | undefined): string => {
    if (!value) return ''

    const numericValue = value.replace(/\D/g, '')

    switch (value.length) {
      case 11:
        return `***.${numericValue.slice(3, 6)}.${numericValue.slice(6, 9)}-**`
      case 14:
        return `**.${numericValue.slice(2, 5)}.${numericValue.slice(
          5,
          8
        )}/****-**`
      default:
        return value
    }
  }

  const getCurrentDate = () => {
    return moment().format('DD/MM/YYYY')
  }

  const getCurrentTime = () => {
    return moment().format('HH:mm')
  }

  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('value', currencyMask(event.target.value))
  }

  const onSubmit = async (data: PixValueStepSchema) => {
    const newAmount = Number(data.value.replace(',', '.'))
    if (newAmount > balance) {
      return showAlertError('Saldo insuficiente')
    }
    if (newAmount <= 0) {
      return showAlertError('O valor deve ser positivo')
    }
    setAmount(newAmount)
    nextStep()
  }

  const [balance, setBalance] = useState(0)

  const getBalanceAccount = async () => {
    const balance = await getBalance()
    if (balance?.value) {
      setBalance(balance?.value)
    } else {
      setBalance(0)
    }
  }

  useEffect(() => {
    getBalanceAccount()
  }, [])

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-[0.625rem] items-center justify-center px-3 py-6 drop-shadow-md rounded"
    >
      <div className="flex items-center justify-between py-2 px-6 bg-[#E0E0E0] rounded-[1.25rem] w-[389px]">
        <div className="flex flex-col items-start justify-center">
          <span className="text-[0.9375rem]">Valor disponível em conta </span>
          <span className="text-[0.9375rem] font-semibold">
            {currencyFormat(balance)}
          </span>
        </div>
      </div>
      <div className="flex flex-col items-center gap-2 justify-center text-[#004A8A]">
        <img src={USDCoinSvg} alt="" />
        <input
          type="text"
          {...register('value')}
          onChange={handleChangeAmount}
          placeholder="R$0,00"
          className="placeholder:text-lg placeholder:text-[#D3D1D1] border border-[#A0A0A0] rounded-[10px] py-3 px-5 placeholder:leading-tight"
        />
        {errors.value && (
          <span className="text-red-500">{errors.value.message}</span>
        )}
      </div>
      <div className="w-[347px] space-y-[10px]">
        <h4>Quem vai receber</h4>
        <div className="flex flex-col gap-4 my-4 py-4">
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Beneficiário</span>
            <span className="text-xs font-semibold">
              {beneficiaryData.name}
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Documento</span>
            <span className="text-xs font-semibold">
              {maskCpfCnpj(beneficiaryData?.beneficiaryDocument)}
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Horário</span>
            <span className="text-xs font-semibold">{getCurrentTime()}</span>
          </div>
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Conta</span>
            <span className="text-xs font-semibold">
              {beneficiaryData.account}
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Data de pagamento</span>
            <span className="text-xs font-semibold">{getCurrentDate()}</span>
          </div>
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Chave</span>
            <span className="text-xs font-semibold">{beneficiaryData.key}</span>
          </div>
        </div>
      </div>
      <button
        type="submit"
        className="text-[0.9375rem] font-bold bg-[#004A8A] w-[475px] text-white h-[55px] flex items-center justify-center rounded-[10px] transition hover:brightness-75"
      >
        Confirmar e continuar
      </button>
    </form>
  )
}
